import { kebab, palette, tokens } from "@sportsmet/theme";
import { type ClassValue, clsx } from "clsx";
import { extendTailwindMerge } from "tailwind-merge";

/**
 * The type-color roles are exposed to Tailwind under `ink*` and the boundary
 * roles under `line*`/`hairline`, rather than their `--sm-color-text*` and
 * `--sm-color-border*` property names. Mirrors `TAILWIND_COLOR_NAME` in
 * `@sportsmet/theme`'s renderer — see the comment there for why.
 */
const TAILWIND_COLOR_NAME: Readonly<Record<string, string>> = {
  text: "ink",
  textSecondary: "ink-secondary",
  textTertiary: "ink-tertiary",
  textInverse: "ink-inverse",
  border: "line",
  borderStrong: "line-strong",
  borderHairline: "hairline",
};

/**
 * tailwind-merge only knows how to resolve a conflict between two classes it
 * recognizes. Out of the box it recognizes Tailwind's stock palette, not ours,
 * so `cn("bg-surface", "bg-primary")` would keep BOTH and let source order in
 * the stylesheet decide — the exact bug `cn` exists to prevent.
 *
 * Feeding it the token names straight from `@sportsmet/theme` means a new
 * semantic color is understood by the merger the moment it is added, with
 * nobody having to remember this file exists.
 */
const semanticColorNames = Object.keys(tokens.color.light).map(
  (key) => TAILWIND_COLOR_NAME[key] ?? kebab(key),
);

const paletteColorNames = Object.entries(palette).flatMap(([family, scale]) =>
  Object.keys(scale).map((step) => `${kebab(family)}-${step}`),
);

const twMerge = extendTailwindMerge({
  extend: {
    theme: {
      color: [...semanticColorNames, ...paletteColorNames],
      radius: Object.keys(tokens.radius),
      shadow: Object.keys(tokens.elevation),
      text: Object.keys(tokens.text).map(kebab),
      ease: Object.keys(tokens.motion.easing),
      spacing: [
        "control-height",
        "control-height-sm",
        "touch-target",
        "icon-sm",
        "icon-md",
        "icon-lg",
        "hairline",
      ],
    },
  },
});

/**
 * Joins class names and resolves Tailwind conflicts, last one winning.
 *
 * Every component in this kit runs its CVA output plus the caller's
 * `className` through this, so a consumer passing `className="bg-error-solid"`
 * to a primary Button actually gets a red button instead of two competing
 * background rules.
 */
export const cn = (...inputs: ClassValue[]): string => twMerge(clsx(inputs));
